/**
 * src/ui/uiConfig/docs/index.ts
 *
 * Single entry point for all UI config in-app documentation.
 *
 * Usage:
 *   import { getDocsForComponent, getDocsForKey } from 'ui/uiConfig/docs'
 *
 * To add docs for a new component:
 *   1. Create a new <name>.docs.ts file in this folder.
 *   2. Export a ComponentDocMap from it.
 *   3. Add it to COMPONENT_DOCS below.
 */

import { absLabsPropDocs } from './absLabs.docs'
import { aliasDocs } from './alias.docs'
import { availabilityPropDocs } from './availability.docs'
import { baseConfigPropDocs } from './baseConfig.docs'
import { cardWidgetPropDocs } from './cardWidget.docs'
import { chartPropDocs } from './chart.docs'
import { commonPropDocs, rootFieldDocs } from './common.docs'
import { containerPropDocs } from './container.docs'
import { datasetTablePropDocs } from './datasetTable.docs'
import { defaultComponentPropDocs } from './defaultComponent.docs'
import { doubleKpiValuePropDocs } from './doubleKpiValue.docs'
import { dsiDocs } from './dsi.docs'
import { editTimeSeriesPropDocs } from './editTimeSeries.docs'
import { elplanBidMatrixPropDocs } from './elplanBidMatrix.docs'
import { elplanBidsPropDocs } from './elplanBids.docs'
import { excelButtonPropDocs } from './excelButton.docs'
import { followUpProdKpiPropDocs } from './followUpProdKpi.docs'
import { followupProductionPropDocs } from './followupProduction.docs'
import { fuelPlanPropDocs } from './fuelPlan.docs'
import { groupingTablePropDocs } from './groupingTable.docs'
import { horizontalBarsPropDocs } from './horizontalBars.docs'
import { kpiValuePropDocs } from './kpiValue.docs'
import { pieChartPropDocs } from './pieChart.docs'
import { realTimeOptimizationBarsPropDocs } from './realTimeOptimizationBars.docs'
import { referenceGuidesPropDocs } from './referenceGuides.docs'
import { reviewForecastsPropDocs } from './reviewForecasts.docs'
import { tablePropDocs } from './table.docs'
import { tabsPropDocs } from './tabs.docs'
import type { ComponentDocMap, PropDoc } from './types'
import { unitAvailabilityPropDocs } from './unitAvailability.docs'

export type { ComponentDocMap, PropDoc }

function getNestedDocForWord(docMap: ComponentDocMap, key: string): PropDoc | null {
  const nestedMatch = Object.entries(docMap).find(
    ([docKey]) => docKey.endsWith(`[].${key}`) || docKey.endsWith(`.${key}`)
  )
  return nestedMatch?.[1] ?? null
}

/** Maps component name → its prop doc map. */
const COMPONENT_DOCS: Record<string, ComponentDocMap> = {
  // Core/documented components
  chart: chartPropDocs,
  kpi_value: kpiValuePropDocs,
  double_kpi_value: doubleKpiValuePropDocs,
  table: tablePropDocs,
  container: containerPropDocs,
  card_widget: cardWidgetPropDocs,
  tabs: tabsPropDocs,
  dataset_table: datasetTablePropDocs,

  // Additional documented components
  base_config: baseConfigPropDocs,
  elplan_bid_matrix: elplanBidMatrixPropDocs,
  pie_chart: pieChartPropDocs,
  horizontal_bars: horizontalBarsPropDocs,
  grouping_table: groupingTablePropDocs,
  excel_button: excelButtonPropDocs,

  // Anchor components
  abs_labs: absLabsPropDocs,
  availability: availabilityPropDocs,
  default_component: defaultComponentPropDocs,
  edit_time_series: editTimeSeriesPropDocs,
  elplan_bids: elplanBidsPropDocs,
  follow_up_prod_kpi: followUpProdKpiPropDocs,
  kpi_value_card: followUpProdKpiPropDocs,
  followup_production: followupProductionPropDocs,
  fuel_plan: fuelPlanPropDocs,
  real_time_optimization_bars: realTimeOptimizationBarsPropDocs,
  reference_guides: referenceGuidesPropDocs,
  review_forecasts: reviewForecastsPropDocs,
  unit_availability: unitAvailabilityPropDocs,

  // Components registered as other components (render identically to their base)
  optimize_production_plan: cardWidgetPropDocs,
  production_plan_forecast: cardWidgetPropDocs,
  production_plan_table: cardWidgetPropDocs,
  followup_forecast: cardWidgetPropDocs,
  sandbox_production_plan: cardWidgetPropDocs,
  shift_handover_chart: cardWidgetPropDocs,
  intraday_trading_view: cardWidgetPropDocs,
  intraday_trading_prototype_view: cardWidgetPropDocs,
  sandbox_forecast: chartPropDocs,
  marginal_cost_chart: chartPropDocs,
  optimizations_info_outcome: chartPropDocs,
  optimizations_info_forecast_diff: chartPropDocs,
  time_series_visualization: chartPropDocs,
  marginal_cost_kpi: kpiValuePropDocs,
  optimize_view: defaultComponentPropDocs,
  optimize_view_variant_a: defaultComponentPropDocs,
  optimize_view_variant_b: defaultComponentPropDocs,
  settlement_view: containerPropDocs,
  day_ahead_overview: containerPropDocs,
  day_ahead_bid_management: containerPropDocs,
  intraday_overview: containerPropDocs,
  ancillary_services: containerPropDocs,
  mfrr_market_results: containerPropDocs,
  samenergi_excel_button: excelButtonPropDocs,
  mfrr_cm_decision_support_graph: chartPropDocs,
  mfrr_eam_decision_support_graph: chartPropDocs,
}

/**
 * Returns the full doc map for a given component, merged with common prop docs.
 * Returns null if the component has no documented props.
 */
export function getDocsForComponent(componentName: string): ComponentDocMap | null {
  const specific = COMPONENT_DOCS[componentName]
  if (!specific) return null
  return { ...commonPropDocs, ...specific }
}

/**
 * Looks up a specific prop doc entry for a component and key.
 * Falls back to common props, root fields, and the alias catalog.
 */
export function getDocForKey(componentName: string, key: string): PropDoc | null {
  const componentDocs = COMPONENT_DOCS[componentName] ?? {}
  return (
    componentDocs[key] ??
    getNestedDocForWord(componentDocs, key) ??
    commonPropDocs[key] ??
    getNestedDocForWord(commonPropDocs, key) ??
    rootFieldDocs[key] ??
    dsiDocs[key] ??
    getNestedDocForWord(dsiDocs, key) ??
    aliasDocs[key] ??
    null
  )
}

/** Returns the shared common prop docs that apply across all components. */
export function getCommonPropDocs(): ComponentDocMap {
  return commonPropDocs
}

/** Returns the root-field doc map (component, display_name, alias, children_ids, etc.). */
export function getRootFieldDocs(): ComponentDocMap {
  return rootFieldDocs
}

/** Returns the full alias catalog. */
export function getAliasDocs(): ComponentDocMap {
  return aliasDocs
}

/** Returns DSI field docs. */
export function getDsiDocs(): ComponentDocMap {
  return dsiDocs
}

/** Returns all registered component names that have documentation. */
export function getDocumentedComponentNames(): string[] {
  return Object.keys(COMPONENT_DOCS)
}
